<template>
  <div class="flex items-center mb-5">
    <div>
      <div class="text-xl">{{ t("members.title") }}</div>
      <div class="text-medium-emphasis text-sm">
        {{ t("members.description") }}
      </div>
    </div>
    <v-spacer></v-spacer>
    <v-btn append-icon="mdi-plus" border @click="showAddDialog = true">{{
      t("members.add")
    }}</v-btn>
  </div>
  <v-table hover density="compact">
    <thead>
      <tr>
        <th class="text-left">{{ t("common.email") }}</th>
        <th class="text-left">{{ t("members.addedAt") }}</th>
        <th class="text-left">{{ t("members.lastSignInAt") }}</th>
        <th class="text-left"></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="member in members" :key="member.uid">
        <td>
          {{ member.email ?? "-" }}
          <v-chip v-if="member.uid === currentUser.uid" class="ml-2" size="small">{{
            t("members.you")
          }}</v-chip>
          <v-chip
            v-if="member.role === 'pendingAccountHolder'"
            class="ml-2"
            size="small"
            color="warning"
            variant="tonal"
            >{{ t("members.pendingVerification") }}</v-chip
          >
          <v-btn
            v-if="
              allowTreezorSimulation &&
              currentUser.isKind('admin') &&
              member.role === 'pendingAccountHolder' &&
              member.onboardingSubmittedAt
            "
            class="ml-2"
            size="small"
            color="primary"
            variant="outlined"
            append-icon="mdi-account-check"
            :loading="simulatingKycUid === member.uid"
            :disabled="!!simulatingKycUid"
            @click="simulateKyc(member.uid)"
            >{{ t("members.simulateKyc") }}</v-btn
          >
        </td>
        <td>{{ getRelativeTime(member.addedAt.toDate()) }}</td>
        <td>
          <span v-if="member.lastSignInAt">
            {{ getRelativeTime(member.lastSignInAt.toDate()) }}
          </span>
          <span v-else class="text-medium-emphasis text-sm">{{ t("users.invitationSent") }}</span>
        </td>
        <td class="text-right">
          <v-btn
            v-if="
              member.uid !== currentUser.uid && member.uid !== accountCreatorUid(account?.users)
            "
            size="small"
            color="error"
            variant="outlined"
            :disabled="removing"
            @click="memberToRemove = member"
            >{{ t("members.remove") }}</v-btn
          >
        </td>
      </tr>
    </tbody>
  </v-table>
  <v-dialog v-model="showAddDialog" max-width="480" persistent>
    <v-card>
      <v-card-title class="flex items-center">
        <span>{{ t("members.addMember") }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          :disabled="adding"
          @click="showAddDialog = false"
        />
      </v-card-title>
      <v-card-text>
        <v-form
          id="add-member-form"
          ref="addMemberForm"
          :disabled="adding"
          validate-on="invalid-input lazy"
          @submit.prevent="addMember"
        >
          <v-text-field
            v-model.trim="email"
            :rules="[required, validEmail]"
            :label="t('common.email')"
            type="email"
            autocomplete="email"
          />
          <div class="text-sm">{{ t("members.inviteEmailInfo") }}</div>
          <v-alert v-if="addError" class="mt-3" color="error" type="error">
            {{ t(`members.${addError}`) }}
          </v-alert>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn :disabled="adding" @click="showAddDialog = false">{{ t("common.cancel") }}</v-btn>
        <v-spacer />
        <v-btn
          type="submit"
          form="add-member-form"
          color="primary"
          :loading="adding"
          :disabled="adding || !!addError"
          variant="flat"
        >
          {{ t("members.add") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog
    :model-value="!!memberToRemove"
    max-width="480"
    :persistent="removing"
    @update:model-value="memberToRemove = null"
  >
    <v-card>
      <v-card-title>{{ t("members.removeMember") }}</v-card-title>
      <v-card-text>{{
        t("members.removeConfirm", { email: memberToRemove?.email ?? "-" })
      }}</v-card-text>
      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-btn :disabled="removing" @click="memberToRemove = null">{{ t("common.close") }}</v-btn>
        <v-spacer />
        <v-btn
          color="error"
          variant="flat"
          :loading="removing"
          :disabled="removing"
          @click="removeMember"
        >
          {{ t("members.remove") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRelativeTime } from "@/composables/useRelativeTime";
import { useRules } from "@/composables/useRules";
import { call, db } from "@/firebase";
import { useToast } from "@/store/toast";
import { useUserStore } from "@/store/user";
import { accountCreatorUid, accountFullPath, type Account } from "@aircap/common/account";
import { userFullPath, type User } from "@aircap/common/user";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import { doc, getDoc } from "firebase/firestore";
import { computed, ref, watch } from "vue";
import { VForm } from "vuetify/components";

const props = defineProps<{
  accountId: string;
}>();

const { t } = useI18n();
const { currentLanguage } = useLanguage();
const currentUser = useUserStore();
const { getRelativeTime } = useRelativeTime();
const { required, validEmail } = useRules();
const toast = useToast();
const allowTreezorSimulation = import.meta.env.VITE_TREEZOR_ALLOW_SIMULATION === "true";

const account = useDocument<Account>(computed(() => doc(db, accountFullPath(props.accountId))));
const usersById = ref<Record<string, User | null>>({});

watch(
  () => Object.keys(account.value?.users ?? {}),
  async (uids) => {
    const missingUids = uids.filter((uid) => !(uid in usersById.value));
    try {
      const snapshots = await Promise.all(
        missingUids.map((uid) => getDoc(doc<User>(db, userFullPath(uid)))),
      );
      for (const snapshot of snapshots) usersById.value[snapshot.id] = snapshot.data() ?? null;
    } catch (cause) {
      const error = new Error(`Error fetching members of account ${props.accountId}`, { cause });
      console.error(error);
      captureException(error);
      toast.showErrorToast();
    }
  },
  { immediate: true },
);

const members = computed(() =>
  Object.entries(account.value?.users ?? {})
    .filter(([uid]) => uid in usersById.value)
    .map(([uid, accountUser]) => ({
      ...accountUser,
      uid,
      email: usersById.value[uid]?.email,
      lastSignInAt: usersById.value[uid]?.lastSignInAt,
    }))
    .sort((a, b) => a.addedAt.toMillis() - b.addedAt.toMillis()),
);

const showAddDialog = ref(false);
const email = ref("");
const adding = ref(false);
const addMemberForm = ref<VForm | null>(null);
const addError = ref<"alreadyMember" | "otherUserKind" | null>(null);
watch(email, () => (addError.value = null));
watch(showAddDialog, (open) => {
  if (open) return;
  addError.value = null;
  addMemberForm.value?.resetValidation();
});

const addMember = async () => {
  if (members.value.some((member) => member.email?.toLowerCase() === email.value.toLowerCase())) {
    addError.value = "alreadyMember";
    return;
  }
  const validateForm = await addMemberForm.value?.validate();
  if (!validateForm?.valid) return;

  try {
    adding.value = true;
    await call("addAccountMember")({
      accountId: props.accountId,
      email: email.value.toLowerCase(),
      lang: currentLanguage.value.code,
    });
    showAddDialog.value = false;
    email.value = "";
  } catch (cause) {
    if (cause instanceof FirebaseError && cause.code === "functions/already-exists") {
      addError.value = "alreadyMember";
      return;
    }
    if (cause instanceof FirebaseError && cause.code === "functions/failed-precondition") {
      addError.value = "otherUserKind";
      return;
    }
    const error = new Error(`Error adding member to account ${props.accountId}`, { cause });
    console.error(error);
    captureException(error);
    toast.showErrorToast();
  } finally {
    adding.value = false;
  }
};

const memberToRemove = ref<(typeof members.value)[number] | null>(null);
const removing = ref(false);
const removeMember = async () => {
  if (!memberToRemove.value) return;
  const { uid } = memberToRemove.value;
  try {
    removing.value = true;
    await call("removeAccountMember")({ accountId: props.accountId, uid });
    memberToRemove.value = null;
    toast.showSuccessToast();
  } catch (cause) {
    const error = new Error(`Error removing member ${uid} from account ${props.accountId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    toast.showErrorToast();
  } finally {
    removing.value = false;
  }
};

const simulatingKycUid = ref<string | null>(null);
const simulateKyc = async (uid: string) => {
  try {
    simulatingKycUid.value = uid;
    await call("simulateTreezorAccountMemberKyc")({ accountId: props.accountId, uid });
    toast.showSuccessToast();
  } catch (cause) {
    const error = new Error(`Error simulating KYC of member ${uid} on account ${props.accountId}`, {
      cause,
    });
    console.error(error);
    captureException(error);
    toast.showErrorToast();
  } finally {
    simulatingKycUid.value = null;
  }
};
</script>
